import QRCode from "qrcode";
import { collection, doc, getCountFromServer, getDoc, query, serverTimestamp, setDoc, updateDoc, where } from "firebase/firestore";
import { db } from "../../firebase";

// Customer sign-up links/QR codes: barfunfinder.com/get?ref=CODE
//   That page shows the code, then sends people to the app (website now,
//   Google Play / App Store once the apps are live). Old links
//   app.barfunfinder.com/?ref=CODE still work.
// People can also TYPE the short code ("SARAH") at sign-up.
//   bar-...  = a bar's own link/QR (counts toward the bar contest)
//   emp-...  = an employee
//   anything else = a campaign (Facebook, Instagram, flyers...)
// Codes live in referralCodes/{code}; each new customer's account is
// stamped with signupRef = CODE by the app.

export const APP_URL = "https://app.barfunfinder.com";
export const GET_URL = "https://barfunfinder.com/get";

/** The part people type at sign-up: emp-sarah -> SARAH. */
export function shortOf(code) {
  return String(code || "").replace(/^(bar|emp)-/, "").toUpperCase();
}

/** "How did you hear about us?" answers (app + dashboard sign-up). */
export const HEARD_LABEL = {
  bar: "A bar",
  team: "Bar Fun Finder team",
  facebook: "Facebook",
  tiktok: "TikTok",
  instagram: "Instagram",
  youtube: "YouTube",
  customer: "A customer",
  friend: "A friend",
  other: "Other",
};

// The bar contest: most new customers from a bar's link/QR wins 6 months of Pro.
export const CONTEST_START = new Date("2026-10-27T00:00:00-05:00");
export const CONTEST_END = new Date("2026-12-15T23:59:59-06:00");
export const CONTEST_PRIZE = "6 months of Bar Fun Finder Pro free";

export function slug(text, max = 30) {
  return String(text || "")
    .toLowerCase()
    .replace(/[‘’']/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, max)
    .replace(/-+$/g, "");
}

export function typeOf(code) {
  if (code.startsWith("bar-")) return "bar";
  if (code.startsWith("emp-")) return "employee";
  return "campaign";
}

export const TYPE_LABEL = { bar: "Bar", employee: "Employee", campaign: "Campaign", none: "No link" };

/** The bar's locked code if it has one, otherwise what it would be from its name. */
export function barCodeFor(bar) {
  if (bar?.referralCode) return bar.referralCode;
  return `bar-${slug(bar?.name || bar?.id || "bar", 34)}`;
}

export function linkFor(code) {
  return `${GET_URL}?ref=${encodeURIComponent(code)}`;
}

// The same code, but for bar owners and charities (dashboard sign-up).
export const PORTAL_URL = "https://portal.barfunfinder.com";
export function partnerLinkFor(code) {
  return `${PORTAL_URL}/signup?ref=${encodeURIComponent(code)}`;
}
export function partnerQrFor(code, size = 600) {
  return QRCode.toDataURL(partnerLinkFor(code), { width: size, margin: 2, errorCorrectionLevel: "M" });
}

/** PNG data URL of a QR code for the link (good for printing). */
export function qrFor(code, size = 600) {
  return QRCode.toDataURL(linkFor(code), { width: size, margin: 2, errorCorrectionLevel: "M" });
}

/**
 * Returns the bar's sign-up code, creating and LOCKING it the first time.
 * Once saved on the bar (bar.referralCode) it never changes, even if the
 * bar is renamed, so printed QR codes always count for the same bar.
 * Two bars with the same name (chains) get different codes.
 */
export async function ensureBarCode(bar) {
  if (bar.referralCode) {
    const ref = doc(db, "referralCodes", bar.referralCode);
    const snap = await getDoc(ref);
    if (!snap.exists() || !snap.data().barId) {
      await setDoc(ref, { code: bar.referralCode, type: "bar", barId: bar.id, label: bar.name || bar.referralCode, createdAt: serverTimestamp() }, { merge: true });
    }
    return bar.referralCode;
  }

  let code = `bar-${slug(bar.name || bar.id || "bar", 34)}`;
  const taken = await getDoc(doc(db, "referralCodes", code));
  if (taken.exists() && taken.data().barId && taken.data().barId !== bar.id) {
    // Another bar already has this name's code: add a short tag from this bar's ID.
    code = `${code}-${slug(String(bar.id).slice(-4), 4)}`;
  }
  await setDoc(doc(db, "referralCodes", code), { code, type: "bar", barId: bar.id, label: bar.name || code, createdAt: serverTimestamp() }, { merge: true });
  await updateDoc(doc(db, "bars", bar.id), { referralCode: code });
  return code;
}

/** How many accounts signed up through this code (counts only, no customer info downloaded). */
export async function signupCount(code) {
  const snap = await getCountFromServer(query(collection(db, "users"), where("signupRef", "==", code)));
  return snap.data().count;
}
